function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Icon from '../Icon';
import Loader from '../Loader';
import Button from '../Button';

require('xps-style/core/toast.css');

var propTypes = {
  className: PropTypes.string,

  /** Type of alert */
  type: PropTypes.oneOf(['success', 'warning', 'error', 'info', 'neutral']),

  /** Icon to be placed in the left box */
  iconName: PropTypes.string,

  /** Text that summaries the information, rendered in slightly more bold text */
  title: PropTypes.string,

  /** Text that is more descriptive to relay more to the user */
  message: PropTypes.string,

  /** Callback function to close the alert and/or to do anything else. To close alert set 'showAlert' prop to false.  */
  onClose: PropTypes.func,

  /** Show close button on the right side of the alert banner */
  showCloseBtn: PropTypes.bool,

  /** Section for action buttons or other content */
  footer: PropTypes.node,

  /** Change the theme from light to dark */
  theme: PropTypes.oneOf(['light', 'dark']),

  /** Use loading spinner or not */
  loading: PropTypes.bool,

  /** Default width is 360px, but can be customized */
  width: PropTypes.number
};
var defaultProps = {
  className: '',
  showCloseBtn: true,
  onClose: function onClose() {},
  theme: 'light',
  loading: false,
  width: 360
};

var Toast = function Toast(_ref) {
  var className = _ref.className,
      type = _ref.type,
      iconName = _ref.iconName,
      title = _ref.title,
      message = _ref.message,
      onClose = _ref.onClose,
      showCloseBtn = _ref.showCloseBtn,
      footer = _ref.footer,
      theme = _ref.theme,
      loading = _ref.loading,
      width = _ref.width;
  var toastClasses = cx(_defineProperty({}, "toast__theme--".concat(theme), true));
  var toastStatusClasses = cx(_defineProperty({
    'toast__icon-box': true
  }, "toast__icon-box--".concat(loading ? 'info' : type), true));
  var toastContainerRef = useRef(null);
  useEffect(function () {
    toastContainerRef.current.style.maxWidth = "".concat(width, "px");
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: toastContainerRef,
    className: "toast__container display-flex ".concat(className, " ").concat(toastClasses, " marginbottom-md")
  }, /*#__PURE__*/React.createElement("div", {
    className: "textalign-center paddingtop-lg ".concat(toastStatusClasses, " ").concat(loading && 'toast__loader')
  }, loading ? /*#__PURE__*/React.createElement(Loader, {
    size: "sm",
    className: "paddingright-md"
  }) : /*#__PURE__*/React.createElement(Icon, {
    name: iconName,
    className: "toast__icon"
  })), /*#__PURE__*/React.createElement("div", {
    className: "toast__content-box"
  }, /*#__PURE__*/React.createElement("div", {
    className: "position-relative display-flex justify-content-between"
  }, /*#__PURE__*/React.createElement("span", {
    className: "fontweight-500 toast__message-area display-inlineblock marginright-md"
  }, title), showCloseBtn && /*#__PURE__*/React.createElement(Button, {
    className: "toast__close-btn",
    size: "small",
    kind: "borderless",
    icon: "only",
    iconName: "close",
    onClick: onClose
  })), message && /*#__PURE__*/React.createElement("div", {
    className: "margintop-sm"
  }, message), footer && /*#__PURE__*/React.createElement("div", {
    className: "margintop-md"
  }, footer)));
};

Toast.defaultProps = defaultProps;
Toast.propTypes = propTypes;
Toast.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Toast",
  "props": {
    "className": {
      "defaultValue": {
        "value": "''",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "showCloseBtn": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Show close button on the right side of the alert banner"
    },
    "onClose": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Callback function to close the alert and/or to do anything else. To close alert set 'showAlert' prop to false."
    },
    "theme": {
      "defaultValue": {
        "value": "'light'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'light'",
          "computed": false
        }, {
          "value": "'dark'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Change the theme from light to dark"
    },
    "loading": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Use loading spinner or not"
    },
    "width": {
      "defaultValue": {
        "value": "360",
        "computed": false
      },
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Default width is 360px, but can be customized"
    },
    "type": {
      "type": {
        "name": "enum",
        "value": [{
          "value": "'success'",
          "computed": false
        }, {
          "value": "'warning'",
          "computed": false
        }, {
          "value": "'error'",
          "computed": false
        }, {
          "value": "'info'",
          "computed": false
        }, {
          "value": "'neutral'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Type of alert"
    },
    "iconName": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Icon to be placed in the left box"
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Text that summaries the information, rendered in slightly more bold text"
    },
    "message": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Text that is more descriptive to relay more to the user"
    },
    "footer": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Section for action buttons or other content"
    }
  }
};
export default Toast;